import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import config from '../config/config';

// Función para generar PDF con QR y nombre del local usando imagen de fondo
export const generateLocalQRPDF = async (local) => {
  try {
    // Crear un nuevo documento PDF
    const pdf = new jsPDF('p', 'mm', 'a4');

    // URL para la evaluación usando la URL de la terminal si existe, o el valor por defecto de config
    const baseUrl = local.terminal_url || config.EVALUACION_URL;
    const formattedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const evaluationUrl = `${formattedBaseUrl}${local.token_publico}`;

    // Generar QR code como data URL
    const qrDataUrl = await QRCode.toDataURL(evaluationUrl, {
      width: 200,
      margin: 2,
      color: {
        dark: '#5A0C62',
        light: '#FFFFFF'
      }
    });

    // Cargar imagen de fondo y logo de terminal
    const backgroundImage = await loadBackgroundImage();
    const terminalLogo = await loadTerminalLogo(local.terminal_logo);

    // Generar PDF con imagen de fondo y logo
    generatePDFWithBackground(pdf, qrDataUrl, local.nombre, backgroundImage, terminalLogo);

    const fileName = `QR_${local.nombre.replace(/[^a-zA-Z0-9]/g, '_')}_${local.token_publico}.pdf`;
    return fileName;

  } catch (error) {
    throw error;
  }
};

// Función para cargar la imagen de fondo
const loadBackgroundImage = () => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL('image/png');
      resolve(dataUrl);
    };
    img.onerror = () => {
      // Si no se puede cargar la imagen, resolver con null
      resolve(null);
    };
    // Ruta de la imagen de fondo
    img.src = '/imagenes/background.png';
  });
};

// Función para cargar el logo de la terminal
const loadTerminalLogo = (terminalLogo) => {
  return new Promise((resolve) => {
    if (!terminalLogo || terminalLogo === 'null' || terminalLogo === 'undefined') {
      resolve(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: img.width,
        height: img.height
      });
    };
    img.onerror = () => resolve(null);
    img.src = `/imagenes/terminales/${terminalLogo}`;
  });
};

const calcularEscalaLogo = (imgWidth, imgHeight, maxWidth, maxHeight) => {
  const ratio = Math.min(
    maxWidth / imgWidth,
    maxHeight / imgHeight
  );

  return {
    width: imgWidth * ratio,
    height: imgHeight * ratio
  };
};

// Función para generar PDF con imagen de fondo y logo
const generatePDFWithBackground = (pdf, qrDataUrl, localName, backgroundImage, terminalLogo) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Agregar imagen de fondo si está disponible
  if (backgroundImage) {
    pdf.addImage(backgroundImage, 'PNG', 0, 0, pageWidth, pageHeight);
  } else {
    // Fondo sólido como fallback
    pdf.setFillColor(248, 249, 250);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  }

  // Agregar logo de terminal en la cabecera si está disponible
  if (terminalLogo) {
    const { width, height } = calcularEscalaLogo(
      terminalLogo.width,
      terminalLogo.height,
      80, // ancho máximo permitido
      50  // alto máximo permitido
    );

    const logoX = (pageWidth - width) / 2;
    const logoY = 30;

    pdf.addImage(
      terminalLogo.dataUrl,
      'PNG',
      logoX,
      logoY,
      width,
      height
    );
  }

  // QR code centrado
  const qrSize = 80;
  const qrX = (pageWidth - qrSize) / 2;
  const qrY = 100;
  pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

  // Configurar fuente para el nombre del local
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(90, 12, 98);
  pdf.setFontSize(20);

  // Nombre del local centrado más cerca del QR
  const nameY = qrY + qrSize + 10; // 10mm debajo del QR
  pdf.text(localName, pageWidth / 2, nameY, { align: 'center' });

  // Guardar el PDF
  const fileName = `QR_${localName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
  pdf.save(fileName);
};

// Función para generar QR masivo con imagen de fondo y logo
export const generateBulkQRPDF = async (locales) => {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Cargar imagen de fondo una sola vez (misma para todos)
    const backgroundImage = await loadBackgroundImage();

    for (let i = 0; i < locales.length; i++) {
      const local = locales[i];
      const baseUrl = local.terminal_url || config.EVALUACION_URL;
      const formattedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
      const evaluationUrl = `${formattedBaseUrl}${local.token_publico}`;

      // Cargar logo de terminal para este local
      const terminalLogo = await loadTerminalLogo(local.terminal_logo);

      // Generar QR para cada local
      const qrDataUrl = await QRCode.toDataURL(evaluationUrl, {
        width: 150,
        margin: 2,
        color: {
          dark: '#5A0C62',
          light: '#FFFFFF'
        }
      });

      // Agregar nueva página para cada QR (excepto la primera)
      if (i > 0) {
        pdf.addPage();
      }

      // Agregar imagen de fondo a cada página
      if (backgroundImage) {
        pdf.addImage(backgroundImage, 'PNG', 0, 0, pageWidth, pageHeight);
      } else {
        pdf.setFillColor(248, 249, 250);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      }

      // Agregar logo de terminal en la cabecera si está disponible
      if (terminalLogo) {
        const { width, height } = calcularEscalaLogo(
          terminalLogo.width,
          terminalLogo.height,
          80, // ancho máximo permitido
          50  // alto máximo permitido
        );

        const logoX = (pageWidth - width) / 2;
        const logoY = 30;

        pdf.addImage(
          terminalLogo.dataUrl,
          'PNG',
          logoX,
          logoY,
          width,
          height
        );
      }

      // Configurar fuente
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(90, 12, 98);

      // QR code centrado
      const qrSize = 80;
      const qrX = (pageWidth - qrSize) / 2;
      const qrY = 100;
      pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

      // Nombre del local centrado debajo del QR
      const nameY = qrY + qrSize + 10;
      pdf.setFontSize(20);
      pdf.text(local.nombre, pageWidth / 2, nameY, { align: 'center' });
    }

    // Guardar el PDF
    const fileName = `QR_Masivo_T_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

    return fileName;
  } catch (error) {
    throw error;
  }
}; 